import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Send, Phone, Globe } from "lucide-react";
import { COMPANY } from "@/lib/site";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || form.message.trim().length < 5) {
      toast.error("Name, email, and message are required");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent. We'll be in touch within one business day.");
      setForm({ name: "", email: "", company: "", phone: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-[#0B1120]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-amber-500" />
              <span className="font-tech text-amber-400 text-xs">DIRECT CONTACT</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              <span className="chrome-text">Get In</span>
              <br />
              <span className="gold-text">Touch.</span>
            </h2>
            <p className="mt-6 font-body text-slate-400 max-w-md">
              Have a quick question? Need a quote? Send us a note and the right division
              specialist will respond.
            </p>

            <div className="mt-10 space-y-5">
              <a
                href={`mailto:${COMPANY.email}`}
                data-testid="contact-email-link"
                className="flex items-center gap-4 group"
              >
                <div className="w-12 h-12 flex items-center justify-center border border-orange-500/30 group-hover:border-orange-500/80 transition-colors">
                  <Mail size={18} className="text-orange-400" />
                </div>
                <div>
                  <div className="font-tech text-[10px] text-slate-500">EMAIL</div>
                  <div className="font-body text-slate-100 group-hover:text-orange-300 transition-colors">
                    {COMPANY.email}
                  </div>
                </div>
              </a>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center border border-slate-700">
                  <Globe size={18} className="text-slate-400" />
                </div>
                <div>
                  <div className="font-tech text-[10px] text-slate-500">WEB</div>
                  <div className="font-body text-slate-300">{COMPANY.domain}</div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center border border-slate-700">
                  <Phone size={18} className="text-slate-400" />
                </div>
                <div>
                  <div className="font-tech text-[10px] text-slate-500">HOURS</div>
                  <div className="font-body text-slate-300">Mon — Fri · 8am to 7pm CST</div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              className="forge-card p-6 md:p-8"
              data-testid="contact-form"
            >
              <div className="font-display text-2xl tracking-tight chrome-text mb-6">
                Send a Message
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="font-tech text-[10px] text-slate-400">NAME *</label>
                  <input
                    data-testid="contact-input-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full mt-1.5 bg-slate-900/60 border border-slate-700 px-3 py-2.5 text-sm text-slate-100 focus:outline-none focus:border-orange-500/60"
                  />
                </div>
                <div>
                  <label className="font-tech text-[10px] text-slate-400">EMAIL *</label>
                  <input
                    data-testid="contact-input-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full mt-1.5 bg-slate-900/60 border border-slate-700 px-3 py-2.5 text-sm text-slate-100 focus:outline-none focus:border-orange-500/60"
                  />
                </div>
                <div>
                  <label className="font-tech text-[10px] text-slate-400">COMPANY</label>
                  <input
                    data-testid="contact-input-company"
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    className="w-full mt-1.5 bg-slate-900/60 border border-slate-700 px-3 py-2.5 text-sm text-slate-100 focus:outline-none focus:border-orange-500/60"
                  />
                </div>
                <div>
                  <label className="font-tech text-[10px] text-slate-400">PHONE</label>
                  <input
                    data-testid="contact-input-phone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full mt-1.5 bg-slate-900/60 border border-slate-700 px-3 py-2.5 text-sm text-slate-100 focus:outline-none focus:border-orange-500/60"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="font-tech text-[10px] text-slate-400">MESSAGE *</label>
                <textarea
                  data-testid="contact-input-message"
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full mt-1.5 bg-slate-900/60 border border-slate-700 px-3 py-2.5 text-sm text-slate-100 focus:outline-none focus:border-orange-500/60 resize-none"
                />
              </div>

              <div className="mt-6 flex items-center justify-between">
                <p className="font-tech text-[10px] text-slate-500 max-w-xs">
                  We reply within one business day.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-btn"
                  className="btn-forge disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? "Sending..." : "Send Message"} <Send size={14} />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
